import saveAs from "file-saver";
import React from "react";
import { getAdminAnalyticsForCharacter } from "../../../api/rest-apis";
import withRouter from "../../../helpers/withRouter";
import DateRangePicker from "../components/date-range-picker";
import AdminHeader from "../components/AdminHeader";
import SingleCharacterChart from "../components/single-character-chart";

interface SingleCharacterAnalyticsProps {
  id: string;
}

interface SingleCharacterAnalyticsState {
  characterData: any;
  characterName: string;
  tags: { tag: string; active: boolean }[];
  selectedFilter: string;
  loading: boolean;
  yAxisValues: number[][]; // Array of arrays of numbers for each dataset
  xAxisLabels: string[]; // Array of strings for the X axis (e.g., months, days, weeks)
  datasetLabels: string[]; // Array of strings for dataset labels
  startDate: Date | null;
  endDate: Date | null;
}

class SingleCharacterAnalytics extends React.Component<
  SingleCharacterAnalyticsProps,
  SingleCharacterAnalyticsState
> {
  state = {
    characterData: {},
    characterName: "",
    tags: [
      { tag: "Basic", active: false, tabName: "Subscriber" },

      { tag: "Plus", active: true, tabName: "Patron" },
      { tag: "Premium", active: false, tabName: "Premium" },
    ],
    yAxisValues: [],
    xAxisLabels: [],
    datasetLabels: [],
    selectedFilter: "Today and Yesterday",
    loading: false,
    startDate: null,
    endDate: null,
  };

  async componentDidMount() {
    await this.fetchData(this.state.selectedFilter);
  }

  async fetchData(
    filter: string,
    customRange: { start: null; end: null } = { start: null, end: null }
  ) {
    this.setState({ loading: true });
    try {
      const response: any = await getAdminAnalyticsForCharacter(this.props.id, {
        plans: this.state.tags
          .filter((tag) => tag.active)
          .map((tag) => tag.tag),
        timeRange: filter,
        customRange,
      });

      this.transformDataForChart(response.graph);
      this.setState({
        characterName: response.characterName,
        characterData: response.graph,
      });
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  }

  async toggleTag(index: number) {
    try {
      const arr = [...this.state.tags];
      arr[index].active = !arr[index].active;
      this.setState({ tags: arr });
      await this.fetchData(this.state.selectedFilter, {
        start: this.state.startDate,
        end: this.state.endDate,
      });
    } catch (error) {
      console.error(error);
    }
  }

  // Add this helper method to your class
  private getMonthDate(
    monthName: string,
    currentYear: number,
    currentMonth: number
  ): Date {
    const monthMap: Record<string, number> = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    const monthIndex = monthMap[monthName];
    if (monthIndex === undefined) return new Date(); // Fallback

    // Determine year: if month is after current month, it's previous year
    const year = monthIndex > currentMonth ? currentYear - 1 : currentYear;
    return new Date(year, monthIndex, 1);
  }

  transformDataForChart(inputData: any) {
    // Get keys and determine if we're dealing with monthly data
    const keys = Object.keys(inputData);
    const isMonthlyData = keys.every(
      (key) => key.includes("-") && key.split("-").length === 2
    );

    let sortedKeys: string[];

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-indexed

    // Special handling for last 12 months
    if (this.state.selectedFilter === "Last 12 months") {
      console.log("Hey");
      sortedKeys = keys.sort((a, b) => {
        const dateA = this.getMonthDate(a, currentYear, currentMonth);
        const dateB = this.getMonthDate(b, currentYear, currentMonth);
        return dateA.getTime() - dateB.getTime(); // Oldest first
      });
    }
    // Special handling for "Today and Yesterday"
    else if (this.state.selectedFilter === "Today and Yesterday") {
      sortedKeys = keys.sort((a, b) => {
        const order = ["Yesterday", "Today"];
        return order.indexOf(a) - order.indexOf(b);
      });
    }
    // For all other cases
    else {
      sortedKeys = keys.sort((a, b) => {
        return new Date(a).getTime() - new Date(b).getTime();
      });
    }

    console.log(sortedKeys);

    // Rest of the transformation logic using sortedKeys
    const xAxisLabels: string[] = [];
    const yAxisValues: number[][] = [];
    const datasetLabels: Set<string> = new Set();

    // Determine unique prompts
    sortedKeys.forEach((key) => {
      inputData[key].forEach((item: any) => {
        datasetLabels.add(item.prompt);
      });
    });

    // Initialize yAxisValues
    const prompts = Array.from(datasetLabels);
    prompts.forEach(() => yAxisValues.push([]));

    // Populate data in sorted order
    sortedKeys.forEach((key) => {
      xAxisLabels.push(key);
      const tokensByPrompt: Record<string, number> = {};

      // Initialize counts
      prompts.forEach((prompt) => (tokensByPrompt[prompt] = 0));

      // Sum tokens
      inputData[key].forEach((payload: any) => {
        tokensByPrompt[payload.prompt] += payload.tokens;
      });

      // Push to dataset arrays
      prompts.forEach((prompt, index) => {
        yAxisValues[index].push(tokensByPrompt[prompt]);
      });
    });

    this.setState({
      xAxisLabels,
      yAxisValues,
      datasetLabels: Array.from(datasetLabels),
      loading: false,
    });
  }

  handleFilterChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const filter: string = event.target.value;
    this.setState({ selectedFilter: filter });
    await this.fetchData(filter); // Fetch data with the new filter
  };

  handleUpdateCustomDate = async (state: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    this.setState({
      startDate: state.startDate,
      endDate: state.endDate,
    });

    const payload: any = {
      start: state.startDate,
      end: state.endDate,
    };

    await this.fetchData(this.state.selectedFilter, payload);
  };

  exportToCSV() {
    const data = this.transformDataForExporting(this.state.characterData);

    const csvData = this.convertToCSV(data);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${this.state.characterName} analytics.csv`);
  }

  convertToCSV(data: any[]): string {
    const header = Object.keys(data[0]).join(",");
    const rows = data.map((item) => Object.values(item).join(","));
    return header + "\n" + rows.join("\n");
  }

  transformDataForExporting(graph: any) {
    const transformedData: any = [];

    // Dynamically loading the keys (dates)
    Object.keys(graph).forEach((time) => {
      // Iterating over each prompt entry
      graph[time].forEach((entry: any) => {
        transformedData.push({
          time, // Dynamically loaded time (day or month)
          prompt: entry.prompt,
          tokens: entry.tokens,
        });
      });
    });

    return transformedData;
  }

  render() {
    return (
      <div className="admin">
        <AdminHeader></AdminHeader>
        <div className="edit-character">
          <div className="title">Character Analytics</div>
          <div className="sub-title mt-3">
            {this.state.characterName}: Usage over time
          </div>

          <div className="flex overflow-x-auto">
            <div className="inline-flex space-x-4 tags">
              {this.state.tags.map((tag: any, index: number) => (
                <div
                  key={index}
                  className={tag.active ? "tag active" : "tag"}
                  onClick={() => this.toggleTag(index)}
                >
                  {tag.tabName}
                </div>
              ))}
            </div>
          </div>

          <div className="bookish-input-group my-4">
            <div className="sm:col-span-3">
              <p className="bookish-input-label">
                Filter <span className="required">*</span>
              </p>
              <div className="mt-2">
                <select
                  id="filter"
                  className="bookish-dropdown-field"
                  name="filter"
                  value={this.state.selectedFilter} // Bind select value to state
                  onChange={this.handleFilterChange} // Update state on change
                >
                  {[
                    "Today and Yesterday",
                    "Last 7 days",
                    "Last 30 days",
                    "This Month",
                    "Last Month",
                    "Last 12 months",
                    "This Year",
                    "Last Year",
                    "All the time",
                  ].map((value: string, index: number) => (
                    <option key={index}>{value}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="relative">
              <button
                style={{
                  background: "#8884D8",
                  color: "#fff",
                  fontWeight: 600,
                  padding: ".5rem",
                  borderRadius: 30,
                  position: "absolute",
                  right: 0,
                }}
                onClick={() => this.exportToCSV()}
              >
                Export
              </button>
            </div>

            <div className="sm:col-span-3">
              <p className="bookish-input-label">Custom</p>
              <DateRangePicker
                callback={(state: any) => this.handleUpdateCustomDate(state)}
              ></DateRangePicker>
            </div>
          </div>

          <div className="flex justify-center"></div>

          {/* {this.state.characterData.length > 0 && (
            <div className="mt-8">
              <SingleCharacterChart characterData={this.state.characterData} />
            </div>
          )} */}
          {!this.state.loading && (
            <SingleCharacterChart
              yAxisValues={this.state.yAxisValues}
              xAxisLabels={this.state.xAxisLabels}
              datasetLabels={this.state.datasetLabels}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(SingleCharacterAnalytics);
